import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();
  #variantController = null;

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
    this.#variantController?.abort();
  }

  /**
   * @param {ProductSelectEvent} event
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    const variantId = event.detail?.variantId;
    const isDjWalletProduct =
      event.target.getAttribute('data-product-handle') === 'original-dj-wallet-final-stock';

    // The DJ Wallet gallery has variant-specific ordering in Liquid. Fetching with only
    // option_values can render a stale variant context, so force the exact variant id.
    if (isDjWalletProduct && variantId) {
      this.#loadExactVariantGallery(String(variantId));
      return;
    }

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Re-render the DJ Wallet gallery from the exact selected variant while preserving
   * preview-theme parameters in the current URL.
   * @param {string} variantId
   */
  async #loadExactVariantGallery(variantId) {
    this.#variantController?.abort();
    const controller = new AbortController();
    this.#variantController = controller;

    try {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variantId);

      const response = await fetch(url.toString(), { signal: controller.signal });
      if (!response.ok) {
        throw new Error(`Failed to fetch selected variant: ${response.status}`);
      }

      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const newMediaGallery = html.querySelector('media-gallery');
      if (!newMediaGallery || controller.signal.aborted) return;

      this.replaceWith(newMediaGallery);
    } catch (error) {
      if (error?.name !== 'AbortError') {
        console.warn('[media-gallery] Exact variant fetch failed:', error);
      }
    } finally {
      if (this.#variantController === controller) {
        this.#variantController = null;
      }
    }
  }

  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Opens the zoom dialog for the selected media.
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event that triggered the zoom.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads the image for the selected media.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
